
'use client';

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { uploadImage } from '@/lib/storage';
import { useAuth } from '@/components/providers/auth-provider';
import Image from 'next/image';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { Edit, Save, X, Loader2 } from 'lucide-react';
import { ImageUploader } from './artworks/image-uploader';
import { useToast } from '@/hooks/use-toast';
import { toCloudflareImage } from '@/lib/utils';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function EditableAboutImage() {
    const { user, userRoles } = useAuth();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();
    const isAdmin = userRoles.includes('admin');

    useEffect(() => {
        const docRef = doc(db, 'site_content', 'about_page');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().imageUrl) {
                setImageUrl(docSnap.data().imageUrl);
            } else {
                setImageUrl('https://placehold.co/150x150.png');
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching about page content:", error);
            setImageUrl('https://placehold.co/150x150.png');
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    
    const handleSave = async () => {
        if (!imageFile) {
            setIsEditing(false);
            return;
        };

        setIsSaving(true);
        try {
            const fileExtension = imageFile.name.split('.').pop();
            const fileName = `about_page_profile-${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `site_content/${fileName}`);
            await uploadImage(storageRef, imageFile);
            const newImageUrl = await getDownloadURL(storageRef);

            const docRef = doc(db, 'site_content', 'about_page');
            await setDoc(docRef, { imageUrl: newImageUrl }, { merge: true });

            toast({ title: "Image Updated", description: "The about page image has been saved." });
            setIsEditing(false);
            setImageFile(null);

        } catch(e) {
            console.error("Failed to save image: ", e);
            toast({ title: "Error", description: "Failed to save the new image.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    }
    
    const handleCancel = () => {
        setIsEditing(false);
        setImageFile(null);
    }

    if (loading) {
        return <Skeleton className="h-[150px] w-[150px] rounded-full mx-auto mb-4 border" />;
    }

    return (
        <div className="relative w-[150px] mx-auto group">
            {isEditing ? (
                <div className="mb-4">
                     <ImageUploader 
                        value={imageFile ? [imageFile] : []} 
                        onChange={(files) => setImageFile(files[0] || null)}
                        onBlur={() => {}}
                        maxFiles={1}
                        maxSize={MAX_FILE_SIZE}
                        currentImageUrls={imageUrl ? [imageUrl] : []}
                    />
                    <div className="flex justify-center gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                            <X className="mr-2 h-4 w-4" /> Cancel
                        </Button>
                         <Button size="sm" onClick={handleSave} disabled={!imageFile || isSaving}>
                            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Save
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <Image
                        src={toCloudflareImage(imageUrl || 'https://placehold.co/150x150.png', 'sm')}
                        alt="Weston Deboer"
                        width={150}
                        height={150}
                        className="rounded-full mx-auto mb-4 border"
                        data-ai-hint="male portrait"
                    />
                     {isAdmin && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="secondary" onClick={() => setIsEditing(true)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Change
                            </Button>
                        </div>
                    )}
                </>
            )}
            
        </div>
    );
}
